import {
  assertDefectClosable,
  DEFECT_KINDS,
  DEFECT_SEVERITIES,
  DomainError,
  EQUIPMENT_STATUSES,
  type ShipTime,
} from '@felix/domain';
import { z } from 'zod';
import type { StatusTone } from '../../StatusBadge.js';
import type { DefectList, DefectStatus, DefectView } from './condition-types.js';
import { shipLocalToUtc } from './counter-form.js';
import type { Sort } from './plan-view.js';

/**
 * Arıza ve breakdown ekranlarının saf hesapları (plan M7 Task 21b; PMS belgesi §15–§16; K24, K27):
 * iş akışı şeridi, türetilmiş durum, süzgeç, sıralama, geçişler ve formların şemaları. Kurallar
 * domain'den (`assertDefectClosable`); sunucu aynı kuralları yeniden uygular.
 */

/**
 * Arıza düzenlenebilir mi: yalnız gemide (K1), düzenleyici bağlıyken ve sunucunun bildirdiği
 * `defects.edit` yetkisiyle. Yetki bilinmiyorsa kapalı; sunucu yetkiyi yine de denetler.
 */
export function defectEditable(input: {
  scope: 'ship' | 'web';
  editor: boolean;
  can: DefectList['can'] | undefined;
}): boolean {
  return input.scope === 'ship' && input.editor && (input.can?.edit ?? false);
}

/** PMS belgesi §15: Defect → Investigation → Repair → Test → Verification → Close. */
export const DEFECT_FLOW = [
  'open',
  'investigating',
  'repair',
  'testing',
  'verification',
  'closed',
] as const;
export type DefectFlowKey = (typeof DEFECT_FLOW)[number];

const STEP: Readonly<Record<string, number>> = {
  open: 0,
  investigating: 1,
  repair: 2,
  testing: 3,
  verification: 4,
};

/** Şeridin adımları: geçilen `done`, bulunulan `current`; kapanmış arızada son adım. */
export function defectFlow(
  defect: Pick<DefectView, 'status' | 'closure'>,
): ('done' | 'current' | null)[] {
  const at = defect.closure.closed ? DEFECT_FLOW.length - 1 : (STEP[defect.status] ?? 0);
  return DEFECT_FLOW.map((_, index) => (index < at ? 'done' : index === at ? 'current' : null));
}

/** Listedeki durum: kapanış onaydan türetilir (durum saklanmaz; K24). */
export type DefectStateKey = DefectStatus | 'pending' | 'returned' | 'closed';

export function defectStateKey(defect: Pick<DefectView, 'status' | 'closure'>): DefectStateKey {
  if (defect.closure.closed) return 'closed';
  if (defect.closure.status === 'pending') return 'pending';
  if (defect.closure.status === 'returned') return 'returned';
  return (STEP[defect.status] === undefined ? 'open' : defect.status) as DefectStatus;
}

const TONES: Readonly<Record<string, StatusTone>> = {
  open: 'danger',
  investigating: 'warning',
  repair: 'warning',
  testing: 'warning',
  verification: 'info',
  pending: 'warning',
  returned: 'danger',
  closed: 'success',
  minor: 'success',
  major: 'warning',
  critical: 'danger',
};

/** Rozet tonu (README tokenları; A17 dili): kritik kırmızı, major sarı, minor yeşil. */
export function defectTone(key: string): StatusTone {
  return TONES[key] ?? 'neutral';
}

export const DEFECT_FILTERS = ['all', 'open', 'pending', 'closed'] as const;
export type DefectFilter = (typeof DEFECT_FILTERS)[number];

/** Süzgeç: "Open" kapanmamış (onay bekleyen dahil); "Pending" kapanış onayında. */
export function matchesDefectFilter(defect: DefectView, filter: DefectFilter): boolean {
  if (filter === 'all') return true;
  const state = defectStateKey(defect);
  if (filter === 'closed') return state === 'closed';
  if (filter === 'pending') return state === 'pending';
  return state !== 'closed';
}

export type DefectSortKey = 'no' | 'component' | 'description' | 'severity' | 'status' | 'reported';
const SEVERITY_RANK: Readonly<Record<string, number>> = { minor: 1, major: 2, critical: 3 };
const STATE_RANK: Readonly<Record<string, number>> = {
  open: 0,
  investigating: 1,
  repair: 2,
  testing: 3,
  verification: 4,
  returned: 5,
  pending: 6,
  closed: 7,
};

export function sortDefects(
  defects: readonly DefectView[],
  sort: Sort<DefectSortKey>,
  names: ReadonlyMap<string, string> = new Map(),
): DefectView[] {
  const value = (d: DefectView): string | number => {
    switch (sort.key) {
      case 'component':
        return names.get(d.equipmentId) ?? '';
      case 'description':
        return d.description;
      case 'severity':
        return SEVERITY_RANK[String(d.severity)] ?? 0;
      case 'status':
        return STATE_RANK[defectStateKey(d)] ?? 0;
      case 'reported':
        return d.reportedAt;
      default:
        return d.defectNo;
    }
  };
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...defects].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const order =
      typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return sign * order || a.defectNo.localeCompare(b.defectNo);
  });
}

/** Aranan metin: numara, açıklama ya da bileşen adı. */
export function searchDefects(
  defects: readonly DefectView[],
  query: string,
  names: ReadonlyMap<string, string>,
): DefectView[] {
  const q = query.trim().toLocaleLowerCase();
  if (q === '') return [...defects];
  return defects.filter((d) =>
    [d.defectNo, d.description, names.get(d.equipmentId) ?? ''].some((text) =>
      text.toLocaleLowerCase().includes(q),
    ),
  );
}

/** Seçilebilir sonraki adımlar (PMS belgesi §16): ileri, test ve doğrulamadan onarıma dönüş. */
export function nextDefectStatuses(status: string): DefectStatus[] {
  const at = STEP[status] ?? 0;
  const forward = (Object.keys(STEP) as DefectStatus[]).filter((key) => (STEP[key] ?? 0) > at);
  const back: DefectStatus[] = status === 'testing' || status === 'verification' ? ['repair'] : [];
  return [...back, ...forward];
}

/* ---------- Arıza formu (PMS belgesi §15; RHF + Zod) ---------- */

export interface DefectFormValues {
  kind: (typeof DEFECT_KINDS)[number];
  equipmentId: string;
  description: string;
  severity: (typeof DEFECT_SEVERITIES)[number];
  operationalImpact: boolean;
  immediateAction: string;
  /** Gemi saatiyle `YYYY-MM-DDTHH:mm`. */
  reportedAt: string;
}

/**
 * Yeni arıza ya da breakdown: ekipman ve açıklama zorunlu; bildirim anı gemi sunucusunun saatine
 * göre gelecekte olamaz (mimari belge §6.11).
 */
export function defectFormSchema(options: { serverNow: string; timeZone: ShipTime }) {
  return z
    .object({
      kind: z.enum(DEFECT_KINDS),
      equipmentId: z.string(),
      description: z.string(),
      severity: z.enum(DEFECT_SEVERITIES),
      operationalImpact: z.boolean(),
      immediateAction: z.string(),
      reportedAt: z.string(),
    })
    .superRefine((values, ctx) => {
      const issue = (path: string, message: string) =>
        ctx.addIssue({ code: 'custom', message, path: [path] });
      if (values.equipmentId === '') issue('equipmentId', 'REQUIRED');
      if (values.description.trim() === '') issue('description', 'DESCRIPTION_REQUIRED');
      const at = shipLocalToUtc(values.reportedAt, options.timeZone);
      if (!at) issue('reportedAt', 'INVALID_DATE');
      else if (Date.parse(at) > Date.parse(options.serverNow)) issue('reportedAt', 'FUTURE_DATE');
    });
}

/* ---------- Kapanış (K24) ---------- */

export interface CloseFormValues {
  /** Part B, kaptanın önlemi (DM-17; ADR 0060 §9). */
  immediateAction: string;
  /** Prosedür gözden geçirme: `yes`, `no`, cevapsız `''` (ADR 0060 §9). */
  procedureReview: '' | 'yes' | 'no';
  procedure: string;
  improvement: string;
  rootCause: string;
  correctiveAction: string;
  testResult: string;
  conditionAfter: string;
  noRepairReason: string;
  classNotificationRef: string;
  classNotificationWaiver: string;
}

export function closeFormOf(defect: DefectView): CloseFormValues {
  const review = defect.procedureReview ?? null;
  return {
    immediateAction: defect.immediateAction ?? '',
    procedureReview: review ? (review.required ? 'yes' : 'no') : '',
    procedure: review?.procedure ?? '',
    improvement: review?.improvement ?? '',
    rootCause: defect.rootCause ?? '',
    correctiveAction: defect.correctiveAction ?? '',
    testResult: defect.testResult ?? '',
    conditionAfter: defect.conditionAfter ?? '',
    noRepairReason: defect.noRepairReason ?? '',
    classNotificationRef: defect.classNotificationRef ?? '',
    classNotificationWaiver: defect.classNotificationWaiver ?? '',
  };
}

const CLOSE_PATH: Readonly<Record<string, string>> = {
  ROOT_CAUSE_REQUIRED: 'rootCause',
  CORRECTIVE_ACTION_REQUIRED: 'correctiveAction',
  REPAIR_NOT_REPORTED: 'repair',
  REPAIR_REQUIRED: 'noRepairReason',
  CONDITION_AFTER_REQUIRED: 'conditionAfter',
  NOT_OPERATIONAL: 'conditionAfter',
  INVALID_CONDITION: 'conditionAfter',
  CLASS_NOTIFICATION_REQUIRED: 'classNotificationRef',
  CAPTAIN_ACTION_REQUIRED: 'immediateAction',
  PROCEDURE_REVIEW_REQUIRED: 'procedureReview',
  PROCEDURE_REVIEW_INCOMPLETE: 'procedure',
};

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

/**
 * Kapanış formu (K24): kök neden ve düzeltici faaliyet [ISM 10.2], onarım iş emri raporlanmış ya da
 * onarım gerekmiyorsa gerekçesi, onarım sonrası durum (çalışmaz olamaz) [NR496 4.3.2d, 4.5.4d],
 * klas ekipmanındaki breakdown'da klas bildirimi [UR Z20 3.3.1]; parola yeniden (§7).
 */
export function closeFormSchema(
  defect: DefectView,
  context: { repairReported: boolean; classEquipment: boolean },
) {
  return z
    .object({
      immediateAction: z.string(),
      procedureReview: z.enum(['', 'yes', 'no']),
      procedure: z.string(),
      improvement: z.string(),
      rootCause: z.string(),
      correctiveAction: z.string(),
      testResult: z.string(),
      conditionAfter: z.string(),
      noRepairReason: z.string(),
      classNotificationRef: z.string(),
      classNotificationWaiver: z.string(),
    })
    .superRefine((values, ctx) => {
      try {
        assertDefectClosable(
          {
            kind: defect.kind === 'breakdown' ? 'breakdown' : 'defect',
            reportedAt: defect.reportedAt,
            tzOffsetMin: 0,
            localDate: defect.localDate,
            description: defect.description,
            severity: defect.severity as 'minor',
            operationalImpact: defect.operationalImpact,
            immediateAction: defect.immediateAction,
            investigation: defect.investigation,
            rootCause: blank(values.rootCause),
            correctiveAction: blank(values.correctiveAction),
            repairWorkOrderId: defect.repairWorkOrderId,
            noRepairReason: blank(values.noRepairReason),
            testResult: blank(values.testResult),
            conditionAfter:
              values.conditionAfter === ''
                ? null
                : (((EQUIPMENT_STATUSES as readonly string[]).includes(values.conditionAfter)
                    ? values.conditionAfter
                    : 'not_operational') as 'operational'),
            classNotificationRef: blank(values.classNotificationRef),
            classNotificationWaiver: blank(values.classNotificationWaiver),
            source: 'manual',
            measurementId: defect.measurementId,
            status: 'verification',
          },
          context,
        );
      } catch (error) {
        if (!(error instanceof DomainError)) throw error;
        ctx.addIssue({
          code: 'custom',
          message: error.code,
          path: [CLOSE_PATH[error.code] ?? 'rootCause'],
        });
      }
      // DM-17 (ADR 0060 §9): Part B ve prosedür gözden geçirme cevabı gemide kapanış ister
      const issue = (code: string, path: string) =>
        ctx.addIssue({ code: 'custom', message: code, path: [path] });
      if (values.immediateAction.trim() === '') {
        issue('CAPTAIN_ACTION_REQUIRED', 'immediateAction');
      }
      if (values.procedureReview === '') issue('PROCEDURE_REVIEW_REQUIRED', 'procedureReview');
      if (
        values.procedureReview === 'yes' &&
        (values.procedure.trim() === '' || values.improvement.trim() === '')
      ) {
        issue('PROCEDURE_REVIEW_INCOMPLETE', 'procedure');
      }
    });
}
